/**
 * hc-aftersell-cart-drawer.js
 *
 * Mục đích:
 *   Khi khách bấm "Add to cart" trong widget AfterSell (Rokt) PDP Upsells
 *   ("She's Better In A Set!"): mở cart drawer của theme và KHÔNG để trang reload,
 *   ĐỒNG THỜI giữ nguyên attribution của AfterSell.
 *
 * Bối cảnh (xác minh bằng chẩn đoán + capture trên store thật, 2026-07):
 *   - Nút native của theme mở drawer qua chuỗi:
 *       theme:cart:add -> addToCart() -> getCart() -> build() -> theme:product:added
 *     Chính `theme:product:added` khiến <cart-drawer> tự mở.
 *   - AfterSell KHÔNG bắn event nào của theme. Nút của họ (React, trong shadow DOM
 *     host=div#aftersell-pdp-upsell, class button.add-button, KHÔNG có <form>) chạy:
 *       onClick -> await fetch('/cart/add.js') -> location.reload().
 *     `location.reload()` là [LegacyUnforgeable] nên không patch được -> chỉ có thể
 *     NGĂN code họ chạy tới dòng reload, bằng cách chặn click.
 *   - Request /cart/add.js của họ KÈM 6 line item property ẩn `__aftersellPdpUpsell__*`.
 *     Đây là CƠ CHẾ ATTRIBUTION (không có beacon client; họ đọc property trên ĐƠN
 *     qua webhook, dùng để gắn tag "AfterSell PDP Upsell" + ghi nhận doanh thu).
 *     => Ta PHẢI gửi đủ 6 property khi tự add, nếu không đơn không được ghi nhận.
 *
 * Nguồn đọc live của 6 property (đã dò khớp với payload thật, đều thuộc APP nên
 * giống nhau giữa các store dùng cùng AfterSell):
 *   product      = "true" (hằng)
 *   customer     = localStorage['aftersellPdpCustomerId']
 *   hostProductId= window.pdpUpsellSelectedProductId
 *   LineItemTags = fiber (nearest) product.lineItemTag / value.lineItemTag  (theo card)
 *   upsellId     = tách từ LineItemTags: __pdpUpsell_<upsellId>_line_item_<N>
 *   deviceType   = suy từ navigator.userAgent
 *   variant id   = fiber (nearest) product.selectedVariant.id / value.selectedVariant.id
 *
 * Cách xử lý — 2 lớp:
 *   LỚP 1 (chính): chặn click nút upsell ở capture phase, đọc 6 nguồn trên, tự gọi
 *     /cart/add.js với ĐỦ property, mở drawer. onClick của AfterSell không chạy ->
 *     không reload. Nếu THIẾU bất kỳ nguồn nào (app đổi cấu trúc) -> KHÔNG chặn,
 *     để AfterSell chạy native (reload nhưng attribution đúng): thoái lui an toàn.
 *   LỚP 2 (lưới an toàn): khi LỚP 1 tắt (INTERCEPT_ENABLED=false) hoặc app khác add.
 *     Bắt /cart/add.js thành công ngoài luồng theme -> đặt cờ pending + mở drawer
 *     (hoặc mở lại sau reload nhờ cờ pending).
 *
 * CẢNH BÁO BẢO TRÌ: LỚP 1 đọc React fiber nội bộ + localStorage của AfterSell —
 *   không phải API công khai. App update có thể đổi. Khi đổi, readCardData()/nguồn
 *   trả thiếu -> tự thoái lui về native (reload). Kiểm tra lại mỗi lần AfterSell update.
 */
(function () {
  'use strict';

  var DEBUG = false;
  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[hc-aftersell]'].concat([].slice.call(arguments)));
  }

  // Công tắc LỚP 1. false = nhường AfterSell native (reload), LỚP 2 vẫn mở drawer.
  var INTERCEPT_ENABLED = true;
  var AFTERSELL_RELOADS = true; // áp cho LỚP 2

  var UPSELL_HOST_ID = 'aftersell-pdp-upsell';
  var UPSELL_BTN_CLASS = 'add-button';

  var ADD_TIMEOUT_MS = 10000;
  var ERR_TIMEOUT = 'Network timed out. Please try again.';
  var ERR_GENERIC = "Sorry, this item couldn't be added.";

  var themeAddInProgress = false;
  var ownAddInProgress = false;
  var themeAddTimer = null;
  var refreshTimer = null;

  var PENDING_KEY = 'hc_open_drawer_pending';
  var clearPendingTimer = null;

  var rawFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  function openDrawer() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  function markThemeAdd() {
    themeAddInProgress = true;
    if (themeAddTimer) clearTimeout(themeAddTimer);
    themeAddTimer = setTimeout(function () {
      themeAddInProgress = false;
    }, 2500);
  }
  function clearThemeAdd() {
    themeAddInProgress = false;
    if (themeAddTimer) {
      clearTimeout(themeAddTimer);
      themeAddTimer = null;
    }
  }
  document.addEventListener('theme:cart:add', markThemeAdd);
  document.addEventListener('theme:product:added', clearThemeAdd);

  function hasCartDrawer() {
    return !!document.querySelector('cart-drawer');
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    var str;
    try {
      str = String(url);
    } catch (e) {
      return false;
    }
    return str.split('?')[0].indexOf('/cart/add') !== -1;
  }

  // =========================================================================
  // LỚP 1: chặn click + tự add với đủ 6 property
  // =========================================================================

  function fiberKey(el) {
    for (var k in el) {
      if (k.indexOf('__reactFiber$') === 0 || k.indexOf('__reactInternalInstance$') === 0) return k;
    }
    return null;
  }

  // Đọc variant id + lineItemTag của CARD đang bấm (nearest fiber). null nếu không thấy.
  function readCardData(btn) {
    var key = fiberKey(btn);
    if (!key) return { variantId: null, lineItemTag: null };
    var fiber = btn[key];
    var hops = 0;
    var variantId = null;
    var lineItemTag = null;
    while (fiber && hops < 15 && (!variantId || !lineItemTag)) {
      var p = fiber.memoizedProps;
      if (p && typeof p === 'object') {
        if (!variantId) {
          var sv = (p.product && p.product.selectedVariant) || (p.value && p.value.selectedVariant);
          if (sv && sv.id) {
            var mm = String(sv.id).match(/(\d{6,})\s*$/);
            if (mm) variantId = mm[1];
          }
        }
        if (!lineItemTag) {
          var lt = (p.product && p.product.lineItemTag) || (p.value && p.value.lineItemTag);
          if (typeof lt === 'string' && lt.indexOf('__pdpUpsell_') === 0) lineItemTag = lt;
        }
      }
      fiber = fiber.return;
      hops++;
    }
    return { variantId: variantId, lineItemTag: lineItemTag };
  }

  function getCustomerId() {
    try {
      return localStorage.getItem('aftersellPdpCustomerId') || null;
    } catch (e) {
      return null;
    }
  }

  function getDeviceType() {
    try {
      return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
    } catch (e) {
      return 'desktop';
    }
  }

  // Ghép đủ dữ liệu để tự add giống AfterSell. Trả null nếu THIẾU nguồn nào ->
  // caller sẽ thoái lui về native (không chặn).
  function buildAddPayload(btn) {
    var card = readCardData(btn);
    var customer = getCustomerId();
    var hostProductId = window.pdpUpsellSelectedProductId;
    if (!card.variantId || !card.lineItemTag || !customer || !hostProductId) {
      log('thiếu nguồn -> thoái lui native:', card, 'customer=', customer, 'host=', hostProductId);
      return null;
    }
    var m = card.lineItemTag.match(/__pdpUpsell_(.+?)_line_item_/);
    var upsellId = m ? m[1] : null;
    if (!upsellId) return null;

    return {
      items: [
        {
          id: Number(card.variantId),
          quantity: 1, // card upsell không có ô chọn số lượng
          properties: {
            __aftersellPdpUpsell__product: 'true',
            __aftersellPdpUpsell__customer: customer,
            __aftersellPdpUpsell__upsellId: upsellId,
            __aftersellPdpUpsell__deviceType: getDeviceType(),
            __aftersellPdpUpsell__hostProductId: String(hostProductId),
            __aftersellPdpUpsell__LineItemTags: card.lineItemTag,
          },
        },
      ],
    };
  }

  // ---- Spinner + báo lỗi (tiêm vào shadow DOM vì CSS theme không với tới) ----
  var WIDGET_CSS =
    '.' + UPSELL_BTN_CLASS + '.hc-loading{color:transparent !important;position:relative;pointer-events:none;}' +
    '.' + UPSELL_BTN_CLASS + '.hc-loading::after{content:"";position:absolute;top:50%;left:50%;' +
    'width:16px;height:16px;margin:-8px 0 0 -8px;border:2px solid transparent;' +
    'border-top-color:var(--hc-spin-color,#fff);border-right-color:var(--hc-spin-color,#fff);' +
    'border-radius:50%;animation:hc-spin .6s linear infinite;box-sizing:border-box;}' +
    '@keyframes hc-spin{to{transform:rotate(360deg);}}' +
    '.hc-add-error{color:#d12727;font-size:12px;line-height:1.3;margin-top:6px;text-align:center;}';

  function ensureWidgetCss(btn) {
    var root = btn && btn.getRootNode ? btn.getRootNode() : null;
    if (!root || !root.host || root.getElementById === undefined) return;
    if (root.querySelector('style[data-hc-widget-css]')) return;
    var st = document.createElement('style');
    st.setAttribute('data-hc-widget-css', '');
    st.textContent = WIDGET_CSS;
    root.appendChild(st);
  }

  function showError(btn, msg) {
    if (!btn) return;
    ensureWidgetCss(btn);
    var box = btn.parentElement || btn;
    var el = box.querySelector('.hc-add-error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'hc-add-error';
      el.setAttribute('role', 'alert');
      box.appendChild(el);
    }
    el.textContent = msg;
    if (el.__hcTimer) clearTimeout(el.__hcTimer);
    el.__hcTimer = setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 6000);
  }
  function clearError(btn) {
    var box = btn && btn.parentElement;
    var el = box && box.querySelector('.hc-add-error');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!INTERCEPT_ENABLED || !rawFetch) return;
      var path = e.composedPath ? e.composedPath() : null;
      if (!path) return;

      var btn = null;
      var inUpsell = false;
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (!n || n.nodeType !== 1) continue;
        if (!btn && n.classList && n.classList.contains(UPSELL_BTN_CLASS)) btn = n;
        if (n.id === UPSELL_HOST_ID) inUpsell = true;
      }
      if (!btn || !inUpsell) return;

      var payload = buildAddPayload(btn);
      if (!payload) {
        // Thiếu nguồn -> KHÔNG chặn. AfterSell add native + reload; LỚP 2 mở drawer.
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      if (btn.disabled) return;

      clearError(btn);
      ensureWidgetCss(btn);
      try {
        btn.style.setProperty('--hc-spin-color', getComputedStyle(btn).color);
      } catch (err) {}
      btn.disabled = true;
      btn.classList.add('hc-loading');

      var settled = false;
      function finish() {
        if (settled) return false;
        settled = true;
        clearTimeout(timer);
        ownAddInProgress = false;
        btn.disabled = false;
        btn.classList.remove('hc-loading');
        return true;
      }
      var timer = setTimeout(function () {
        if (finish()) showError(btn, ERR_TIMEOUT);
      }, ADD_TIMEOUT_MS);

      ownAddInProgress = true;
      rawFetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (res.ok) {
            if (finish()) openDrawer();
            return;
          }
          return res.json().then(
            function (body) {
              if (finish()) showError(btn, (body && (body.description || body.message)) || ERR_GENERIC);
            },
            function () {
              if (finish()) showError(btn, ERR_GENERIC);
            }
          );
        })
        .catch(function (err) {
          if (finish()) {
            showError(btn, ERR_GENERIC);
            log('add thất bại:', err);
          }
        });
    },
    true
  );

  // =========================================================================
  // LỚP 2: lưới an toàn
  // =========================================================================
  function handleExternalAdd() {
    if (ownAddInProgress) return;
    if (themeAddInProgress) return;
    if (window.location && window.location.pathname === '/cart') return;

    try {
      sessionStorage.setItem(PENDING_KEY, String(Date.now()));
    } catch (e) {}
    if (clearPendingTimer) clearTimeout(clearPendingTimer);
    clearPendingTimer = setTimeout(function () {
      try {
        sessionStorage.removeItem(PENDING_KEY);
      } catch (e) {}
    }, 3000);

    if (!hasCartDrawer()) return;
    if (AFTERSELL_RELOADS) return;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(openDrawer, 80);
  }

  function consumePendingOpen() {
    var raw;
    try {
      raw = sessionStorage.getItem(PENDING_KEY);
      if (raw) sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var ts = parseInt(raw, 10);
    if (!ts || Date.now() - ts > 10000) return;
    if (window.location && window.location.pathname === '/cart') return;
    if (!hasCartDrawer()) return;
    setTimeout(openDrawer, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', consumePendingOpen);
  } else {
    consumePendingOpen();
  }

  // ---- Patch fetch (LỚP 2 quan sát add ngoài luồng theme) -----------------
  if (rawFetch) {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var isAdd = isCartAddUrl(url);
      var promise = originalFetch.apply(this, arguments);
      if (isAdd) {
        promise
          .then(function (response) {
            if (response && response.ok) handleExternalAdd();
          })
          .catch(function () {});
      }
      return promise;
    };
  }

  // ---- Patch XMLHttpRequest (LỚP 2) ---------------------------------------
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var xhrProto = window.XMLHttpRequest.prototype;
    var originalOpen = xhrProto.open;
    var originalSend = xhrProto.send;
    xhrProto.open = function (method, url) {
      this.__hcIsCartAdd = isCartAddUrl(url);
      return originalOpen.apply(this, arguments);
    };
    xhrProto.send = function () {
      if (this.__hcIsCartAdd) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) handleExternalAdd();
        });
      }
      return originalSend.apply(this, arguments);
    };
  }
})();
